import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import dayjs from "dayjs";
import { getToken } from "@/utils/auth";
import ProtectedRoute from "@/components/ProtectedRoute";
import RoleGate from "@/components/RoleGate";
import Button from "@/components/Base/Button";
import { FormInput } from "@/components/Base/Form";
import Lucide from "@/components/Base/Lucide";
import Litepicker from "@/components/Base/Litepicker";
import axios from "axios";

const PRODUCT_TYPE_ORDER = ["Devices", "Refills", "Accessories"];

type ReceiptLine = {
  id: string;
  productId: string;
  quantity: number;
  product: { id: string; name: string; sku?: string | null; type?: string | null; productImageUrl?: string | null };
};

type Receipt = {
  id: string;
  batchNo: string;
  receivedAt: string;
  refNo: string | null;
  documentUrl: string | null;
  lines: ReceiptLine[];
};

export default function EditStockInPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const companyIdFromUrl = searchParams.get("companyId");

  const [receipt, setReceipt] = useState<Receipt | null>(null);
  const [loading, setLoading] = useState(true);
  const [receivedAt, setReceivedAt] = useState("");
  const [refNo, setRefNo] = useState("");
  const [documentUrl, setDocumentUrl] = useState("");
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [docError, setDocError] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [quantities, setQuantities] = useState<Record<string, number>>({});
  const [exchangeQuantities, setExchangeQuantities] = useState<Record<string, number>>({});
  const [exchangeInputEnabled, setExchangeInputEnabled] = useState<Record<string, boolean>>({});
  const [productBalances, setProductBalances] = useState<Record<string, number>>({});
  const [expandedTypeIds, setExpandedTypeIds] = useState<Set<string>>(new Set());
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [submitSuccess, setSubmitSuccess] = useState("");

  const apiUrl = import.meta.env.VITE_API_URL || "http://localhost:4000";

  const linesByType = useMemo(() => {
    if (!receipt?.lines?.length) return [];
    const uniqueByProduct = new Map<string, ReceiptLine>();
    for (const line of receipt.lines) {
      if (!line.productId) continue;
      if (!uniqueByProduct.has(line.productId)) {
        uniqueByProduct.set(line.productId, line);
      }
    }
    const map = new Map<string, ReceiptLine[]>();
    for (const line of uniqueByProduct.values()) {
      const typeLabel = (line.product?.type || "").trim() || "Other";
      if (!map.has(typeLabel)) map.set(typeLabel, []);
      map.get(typeLabel)!.push(line);
    }
    const ordered: [string, ReceiptLine[]][] = [];
    for (const name of PRODUCT_TYPE_ORDER) {
      const found = Array.from(map.entries()).find(([k]) => k.trim().toLowerCase() === name.toLowerCase());
      if (found) ordered.push(found);
    }
    for (const [label, lines] of map.entries()) {
      if (!PRODUCT_TYPE_ORDER.some((n) => n.toLowerCase() === label.trim().toLowerCase())) ordered.push([label, lines]);
    }
    return ordered;
  }, [receipt?.lines]);

  useEffect(() => {
    if (!receipt?.lines?.length) return;
    const typeLabels = linesByType.map(([label]) => label);
    setExpandedTypeIds((prev) => {
      const next = new Set(prev);
      typeLabels.forEach((l) => next.add(l));
      return next;
    });
  }, [receipt?.id, receipt?.lines?.length]);
  const token = getToken();
  const queryCompany = companyIdFromUrl ? `?companyId=${companyIdFromUrl}` : "";

  useEffect(() => {
    if (!id) return;
    (async () => {
      try {
        setLoading(true);
        const res = await axios.get(`${apiUrl}/stock-in/${id}${queryCompany}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = res.data as Receipt;
        setReceipt(data);
        setReceivedAt(data.receivedAt ? dayjs(data.receivedAt).format("YYYY-MM-DD") : dayjs().format("YYYY-MM-DD"));
        setRefNo(data.refNo || "");
        setDocumentUrl(data.documentUrl || "");
        const q: Record<string, number> = {};
        const ex: Record<string, number> = {};
        const exEnabled: Record<string, boolean> = {};
        for (const line of data.lines || []) {
          if (line.quantity > 0) q[line.productId] = (q[line.productId] ?? 0) + line.quantity;
          if (line.quantity < 0) {
            ex[line.productId] = (ex[line.productId] ?? 0) + Math.abs(line.quantity);
            exEnabled[line.productId] = true;
          }
        }
        setQuantities(q);
        setExchangeQuantities(ex);
        setExchangeInputEnabled(exEnabled);
      } catch (err) {
        console.error("Failed to load Stock IN receipt", err);
        setReceipt(null);
      } finally {
        setLoading(false);
      }
    })();
  }, [id, apiUrl, queryCompany, token]);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${apiUrl}/stock-out/product-balances${queryCompany}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (Array.isArray(res.data)) {
          const map: Record<string, number> = {};
          res.data.forEach((row: { productId: string; balance: number }) => {
            map[row.productId] = row.balance;
          });
          setProductBalances(map);
        }
      } catch (err) {
        console.error("Failed to load product balances", err);
      }
    })();
  }, [apiUrl, queryCompany, token]);

  const setQuantity = useCallback((productId: string, value: number) => {
    setQuantities((prev) => ({ ...prev, [productId]: value >= 0 ? value : 0 }));
  }, []);
  const setExchangeQuantity = useCallback((productId: string, value: number) => {
    setExchangeQuantities((prev) => ({ ...prev, [productId]: value >= 0 ? value : 0 }));
  }, []);
  const toggleExchangeInput = useCallback((productId: string) => {
    setExchangeInputEnabled((prev) => {
      const enabled = !!prev[productId];
      const next = { ...prev, [productId]: !enabled };
      if (enabled) {
        setExchangeQuantities((qPrev) => ({ ...qPrev, [productId]: 0 }));
      }
      return next;
    });
  }, []);

  const toggleType = useCallback((typeLabel: string) => {
    setExpandedTypeIds((prev) => {
      const next = new Set(prev);
      if (next.has(typeLabel)) next.delete(typeLabel);
      else next.add(typeLabel);
      return next;
    });
  }, []);

  const handleDocumentChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setDocError("");
    setUploadingDoc(true);
    try {
      const formData = new FormData();
      formData.append("document", file);
      const res = await axios.post(`${apiUrl}/upload/document`, formData, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" }
      });
      const url = res.data?.data?.url ?? res.data?.data?.key ?? "";
      setDocumentUrl(url);
    } catch (err: unknown) {
      const msg = axios.isAxiosError(err) && err.response?.data?.message ? err.response.data.message : "Upload failed";
      setDocError(String(msg));
      setDocumentUrl("");
    } finally {
      setUploadingDoc(false);
    }
  };

  const handleRemoveDocument = () => {
    setDocumentUrl("");
    setDocError("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id) return;
    setSubmitError("");
    setSubmitSuccess("");
    if (!receivedAt.trim()) {
      setSubmitError("Please select date.");
      return;
    }
    const lines = Object.entries(quantities)
      .filter(([, qty]) => qty > 0)
      .map(([productId, quantity]) => ({ productId, quantity }));
    const exchangeLines = Object.entries(exchangeQuantities)
      .filter(([, qty]) => qty > 0)
      .map(([productId, quantity]) => ({ productId, quantity }));
    if (lines.length === 0 && exchangeLines.length === 0) {
      setSubmitError("Please enter at least one Stock IN or Stock EXCHANGE quantity.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.put(
        `${apiUrl}/stock-in/${id}${queryCompany}`,
        {
          receivedAt: new Date(receivedAt).toISOString(),
          refNo: refNo.trim() || undefined,
          documentUrl: documentUrl.trim() || undefined,
          lines,
          exchangeLines,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSubmitSuccess("Stock IN updated successfully.");
      setTimeout(() => {
        navigate(companyIdFromUrl ? `/stock-in?companyId=${companyIdFromUrl}` : "/stock-in");
      }, 2000);
    } catch (err: unknown) {
      const data = axios.isAxiosError(err) ? err.response?.data : undefined;
      const msg = data?.message ?? data?.error ?? "Failed to update";
      setSubmitError(String(msg));
    } finally {
      setSubmitting(false);
    }
  };

  const handleBack = () => {
    const to = companyIdFromUrl ? `/stock-in?companyId=${companyIdFromUrl}` : "/stock-in";
    navigate(to);
  };

  if (loading) {
    return (
      <ProtectedRoute>
        <RoleGate allow={["SUPERADMIN", "ADMIN"]}>
          <div className="flex items-center justify-center py-20">
            <Lucide icon="Loader2" className="w-8 h-8 animate-spin text-slate-500" />
          </div>
        </RoleGate>
      </ProtectedRoute>
    );
  }

  if (!receipt) {
    return (
      <ProtectedRoute>
        <RoleGate allow={["SUPERADMIN", "ADMIN"]}>
          <div className="p-5 text-center text-slate-500">Stock IN record not found.</div>
          <Button variant="outline-secondary" onClick={() => navigate(companyIdFromUrl ? `/stock-in?companyId=${companyIdFromUrl}` : "/stock-in")}>
            Back to list
          </Button>
        </RoleGate>
      </ProtectedRoute>
    );
  }

  return (
    <ProtectedRoute>
      <RoleGate allow={["SUPERADMIN", "ADMIN"]}>
        <div className="grid grid-cols-12 gap-y-10 gap-x-6 w-full">
          <div className="col-span-12">
            <div className="flex flex-col mt-4 md:mt-0 md:h-10 gap-y-3 md:items-center md:flex-row">
              <div className="text-base font-medium group-[.mode--light]:text-white">Edit Stock IN</div>
              <div className="flex flex-col sm:flex-row gap-x-3 gap-y-2 md:ml-auto">
                <Button variant="outline-secondary" onClick={handleBack}>
                  <Lucide icon="ArrowLeft" className="stroke-[1.3] w-4 h-4 mr-2" />
                  Back to list
                </Button>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="mt-3.5 flex flex-col gap-6">
              <div className="box p-5">
                <div className="text-[0.94rem] font-medium pb-4 border-b border-slate-200/60 dark:border-darkmode-400">
                  Delivery Order details
                </div>
                {submitError && (
                  <div className="mt-5 flex items-center gap-2 rounded-[0.6rem] border border-danger/30 bg-danger/5 px-4 py-3 text-danger">
                    <Lucide icon="AlertTriangle" className="w-6 h-6 shrink-0" />
                    {submitError}
                  </div>
                )}
                {submitSuccess && (
                  <div className="mt-5 flex items-center gap-2 rounded-[0.6rem] border border-success/30 bg-success/5 px-4 py-3 text-success">
                    <Lucide icon="CheckCircle" className="w-6 h-6 shrink-0" />
                    {submitSuccess}
                  </div>
                )}
                <div className="mt-5 flex flex-wrap items-end gap-6">
                  <div className="flex items-center gap-2">
                    <label className="shrink-0 text-sm">Date</label>
                    <div className="relative inline-flex">
                      <Lucide icon="Calendar" className="absolute group-[.mode--light]:!text-slate-800 inset-y-0 left-0 z-10 w-4 h-4 my-auto ml-3 stroke-[1.3]" />
                      <Litepicker
                        value={receivedAt}
                        onChange={(e) => setReceivedAt(e.target.value)}
                        options={{
                          autoApply: true,
                          singleMode: true,
                          showWeekNumbers: false,
                          dropdowns: { minYear: 2020, maxYear: null, months: true, years: true },
                          format: "YYYY-MM-DD"
                        }}
                        className="w-full min-w-[140px] max-w-[160px] pl-9 pr-3 py-2 rounded-[0.5rem] border border-slate-300/60 shadow-sm group-[.mode--light]:!bg-white/[0.12] group-[.mode--light]:!text-slate-800 dark:group-[.mode--light]:!bg-darkmode-900/30 dark:!border-darkmode-400 dark:!box text-sm"
                      />
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="shrink-0 text-sm">Batch No</label>
                    <FormInput type="text" value={receipt.batchNo} readOnly className="w-[140px] min-w-0 bg-slate-100 dark:bg-darkmode-800" />
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="shrink-0 text-sm">Ref No</label>
                    <FormInput
                      type="text"
                      placeholder="Your ref. no"
                      value={refNo}
                      onChange={(e) => setRefNo(e.target.value)}
                      className="min-w-[120px] w-36"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="shrink-0 text-sm">Document</label>
                    <div className="flex items-center gap-3 flex-wrap">
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept=".pdf,application/pdf,image/*,.doc,.docx"
                        onChange={handleDocumentChange}
                        className="text-sm text-slate-600 file:mr-3 file:py-2 file:px-3 file:rounded file:border-0 file:bg-slate-100 file:text-slate-700"
                      />
                      {uploadingDoc && (
                        <span className="flex items-center text-sm text-slate-500">
                          <Lucide icon="Loader2" className="w-4 h-4 animate-spin mr-1" /> Uploading...
                        </span>
                      )}
                      {documentUrl && (
                        <>
                          <span className="text-sm text-success flex items-center">
                            <Lucide icon="CheckCircle" className="w-4 h-4 mr-1" /> Document uploaded
                          </span>
                          <button type="button" onClick={handleRemoveDocument} className="text-sm text-slate-500 hover:text-danger underline">
                            Remove
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                  <Button type="submit" variant="primary" disabled={submitting} className="!bg-[#2D2926] hover:!bg-[#2D2926]/90 shrink-0">
                    {submitting && <Lucide icon="Loader2" className="w-4 h-4 mr-2 animate-spin" />}
                    Update Stock IN
                  </Button>
                </div>
                {docError && <div className="mt-3 text-danger text-xs">{docError}</div>}
              </div>

              {linesByType.length === 0 ? (
                <div className="box p-10 text-center text-slate-500">No product lines.</div>
              ) : (
                linesByType.map(([typeLabel, lines]) => {
                  const isExpanded = expandedTypeIds.has(typeLabel);
                  return (
                    <div key={typeLabel} className="box overflow-hidden">
                      <button
                        type="button"
                        className="flex items-center gap-3 w-full text-left p-5 hover:bg-slate-50/50 dark:hover:bg-darkmode-400/30 transition-colors"
                        onClick={() => toggleType(typeLabel)}
                        aria-expanded={isExpanded}
                      >
                        <div className="text-[0.94rem] font-medium">{typeLabel}</div>
                        <Lucide
                          icon={isExpanded ? "ChevronUp" : "ChevronDown"}
                          className="w-5 h-5 stroke-[1.3] ml-auto text-slate-500 shrink-0"
                        />
                      </button>
                      {isExpanded && (
                        <div className="px-12 pb-6 pt-0 border-t border-slate-200/60 dark:border-darkmode-400">
                          <div className="grid grid-cols-[5fr_2fr_2.5fr] gap-4 items-center py-2 border-b border-slate-200/40 dark:border-darkmode-400/60 text-xs font-medium text-slate-500">
                            <div>Product</div>
                            <div className="text-center">IN</div>
                            <div className="text-center">Stock EXCHANGE</div>
                          </div>
                          {lines.map((line) => (
                              <div
                                key={line.productId}
                                className="grid grid-cols-[5fr_2fr_2.5fr] gap-4 items-center py-2 border-b border-slate-200/40 dark:border-darkmode-400/60 last:border-b-0"
                              >
                                <div className="flex items-center gap-3 min-w-0">
                                  <div className="w-12 h-12 shrink-0 rounded overflow-hidden bg-slate-100 dark:bg-darkmode-700 flex items-center justify-center">
                                    {line.product?.productImageUrl ? (
                                      <img src={line.product.productImageUrl} alt="" className="w-full h-full object-contain" />
                                    ) : (
                                      <Lucide icon="Package" className="w-6 h-6 text-slate-400" />
                                    )}
                                  </div>
                                  <div className="min-w-0">
                                    <div className="text-sm font-medium truncate">{line.product?.name ?? "—"}</div>
                                    {line.product?.sku && <div className="text-xs text-slate-500">{line.product.sku}</div>}
                                  </div>
                                </div>
                                <div className="flex justify-center items-center">
                                  <FormInput
                                    type="number"
                                    min={0}
                                    step={1}
                                    placeholder="0"
                                    className="w-full max-w-24 min-w-0"
                                    value={quantities[line.productId] ?? ""}
                                    onChange={(e) => setQuantity(line.productId, parseInt(e.target.value, 10) || 0)}
                                  />
                                </div>
                                <div className="flex justify-center items-center">
                                  <div className="flex items-center gap-1 w-full max-w-32 min-w-0">
                                    <Button
                                      type="button"
                                      variant="outline-secondary"
                                      className="h-[34px] px-2 text-[10px] leading-none"
                                      onClick={() => toggleExchangeInput(line.productId)}
                                    >
                                      {exchangeInputEnabled[line.productId] ? "Off" : "Enable"}
                                    </Button>
                                    <FormInput
                                      type="number"
                                      min={0}
                                      step={1}
                                      placeholder="0"
                                      className="w-full max-w-20 min-w-0"
                                      value={exchangeQuantities[line.productId] ?? ""}
                                      onChange={(e) => setExchangeQuantity(line.productId, parseInt(e.target.value, 10) || 0)}
                                      disabled={!exchangeInputEnabled[line.productId]}
                                    />
                                  </div>
                                </div>
                              </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </form>
          </div>
        </div>
      </RoleGate>
    </ProtectedRoute>
  );
}
